<template>
  <div class="design-element-preview" ref="container">
    <div class="design-element-preview__viewport" :style="{ height: `${scaledHeight}px` }">
      <iframe
        v-if="visible"
        ref="frame"
        class="design-element-preview__frame"
        :title="title"
        :srcdoc="srcdoc"
        :style="frameStyle"
        @load="onFrameLoad"></iframe>
    </div>
  </div>
</template>

<script>
  import shortcodeStyle from "!raw-loader!sass-loader!@core/preset/preset/shortcodes.scss";

  const FONT_FACES = `
    @import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:wght@400;700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
  `;

  /**
   * Elements are written against the page's content column, and several of them deliberately
   * break out of it (`.select-one-row` widens itself by 264px). Reproducing the column inside a
   * full browser viewport is what makes those elements render the way they do on a real page.
   */
  const PAGE_COLUMN_WIDTH = 730;
  const DESKTOP_VIEWPORT_WIDTH = 1440;
  const MOBILE_VIEWPORT_WIDTH = 390;
  const EXAMPLE_VIEWPORT_HEIGHT = 420;
  // Wide enough that a frame is rebuilt before it scrolls into view, narrow enough that only the
  // few around the viewport keep a running page app.
  const FRAME_ROOT_MARGIN = "600px";

  /**
   * A preview is for looking at, so nothing inside it may navigate: the elements carry real links
   * and the quizzes redirect once answered. Links and form submits are swallowed, popups are
   * stubbed out, and the frame is left showing the element either way.
   */
  const NAVIGATION_GUARD = `<script>
    window.open = () => null;
    document.addEventListener("click", event => {
      if (event.target.closest("a")) event.preventDefault();
    }, true);
    document.addEventListener("submit", event => event.preventDefault(), true);
  </${"script"}>`;

  const FRAME_RESET = `
    body { margin: 0; padding: 16px 0; overflow-x: hidden; background-color: #fff; }
    .design-element-preview__column { margin: 0 auto; max-width: ${PAGE_COLUMN_WIDTH}px; }
    img, video { max-width: 100%; height: auto; }
  `;

  export default {
    name: "DesignElementPreview",
    props: {
      html: {
        type: String,
        required: true,
      },
      title: {
        type: String,
        required: true,
      },
      mobile: {
        type: Boolean,
        default: false,
      },
      /**
       * With `example` set, the frame loads the page's own scripts so the element renders as the
       * live Vue component instead of the editor placeholder.
       */
      example: {
        type: Boolean,
        default: false,
      },
      runtimeScripts: {
        type: Array,
        default: () => [],
      },
      runtimeStyles: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        visible: false,
        containerWidth: 0,
        contentHeight: 0,
        bodyObserver: null,
        containerObserver: null,
        viewportObserver: null,
        observedContainer: null,
      };
    },
    computed: {
      pageCss() {
        return this.$store.state.tinymceCss;
      },
      viewportWidth() {
        return this.mobile ? MOBILE_VIEWPORT_WIDTH : DESKTOP_VIEWPORT_WIDTH;
      },
      scale() {
        return Math.min(1, this.containerWidth / this.viewportWidth);
      },
      frameHeight() {
        // Overlay elements (the toast, the bottom bar) pin themselves to the viewport and so
        // contribute no document height. In example mode the frame keeps a viewport for them.
        return this.example ? Math.max(this.contentHeight, EXAMPLE_VIEWPORT_HEIGHT) : this.contentHeight;
      },
      scaledHeight() {
        return this.frameHeight * this.scale;
      },
      frameStyle() {
        const offset = (this.containerWidth - this.viewportWidth * this.scale) / 2;

        return {
          width: `${this.viewportWidth}px`,
          height: `${this.frameHeight}px`,
          transform: `translateX(${offset}px) scale(${this.scale})`,
        };
      },
      srcdoc() {
        const styles = `${FONT_FACES}${shortcodeStyle}${this.pageCss}${FRAME_RESET}`;
        const column = `<div class="design-element-preview__column">${this.html}</div>`;

        if (!this.example || !this.runtimeScripts.length) {
          return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${styles}</style></head><body>${NAVIGATION_GUARD}${column}</body></html>`;
        }

        // The page's app entry expects the scaffold a rendered page gives it: a colour-carrying
        // script tag it reads then removes, and the two roots it mounts on.
        const scaffold = `<script data-primary-color=""></${"script"}><div id="app">${column}</div><div id="footer-app"></div>`;
        const scripts = this.runtimeScripts.map(src => `<script src="${src}"></${"script"}>`).join("");
        // Linked ahead of the inline block so the editor stylesheet still wins: these carry the
        // page's own Tailwind build, whose preflight would otherwise reset it from further down.
        const runtimeCss = this.runtimeStyles.map(href => `<link rel="stylesheet" href="${href}">`).join("");
        // The components ask for their own assets by root-relative path, which a preview frame
        // would otherwise resolve against the dashboard rather than the page.
        const base = `<base href="${process.env.VUE_APP_API}/">`;

        return `<!DOCTYPE html><html><head><meta charset="utf-8">${base}${runtimeCss}<style>${styles}</style></head><body>${NAVIGATION_GUARD}${scaffold}${scripts}</body></html>`;
      },
    },
    mounted() {
      this.containerObserver = new ResizeObserver(() => {
        this.containerWidth = this.$refs.container.clientWidth;
      });

      // The library renders every element at once and an example frame runs the whole page app,
      // so a frame only exists while its preview is near the viewport. The last measured height
      // stays behind, which keeps the list from jumping as frames come and go.
      this.viewportObserver = new IntersectionObserver(
        entries => {
          const visible = entries[entries.length - 1].isIntersecting;

          if (!visible) this.bodyObserver?.disconnect();
          this.visible = visible;
        },
        { rootMargin: FRAME_ROOT_MARGIN },
      );

      this.observeContainer();
    },
    updated() {
      // A patch can swap the root element, which would leave both observers watching a node that
      // is no longer in the document and stop the frame ever being built.
      if (this.observedContainer !== this.$refs.container) this.observeContainer();
    },
    beforeDestroy() {
      this.viewportObserver?.disconnect();
      this.containerObserver?.disconnect();
      this.bodyObserver?.disconnect();
    },
    methods: {
      observeContainer() {
        this.observedContainer = this.$refs.container;
        this.containerObserver.disconnect();
        this.containerObserver.observe(this.observedContainer);
        this.containerWidth = this.observedContainer.clientWidth;
        this.viewportObserver.disconnect();
        this.viewportObserver.observe(this.observedContainer);
      },
      onFrameLoad() {
        // Nothing stops a component assigning `window.location`, and a preview frame resolves a
        // relative target against the page's own base - which would load another site inside the
        // preview. If the frame ever leaves its own document, put the element back. A navigation
        // to the API origin leaves `contentDocument` unreadable, which is itself the signal.
        const frameDocument = this.$refs.frame.contentDocument;

        if (!frameDocument || !frameDocument.location.href.startsWith("about:")) {
          this.$refs.frame.srcdoc = this.srcdoc;

          return;
        }

        this.updateHeight();
        this.bodyObserver?.disconnect();
        // Web fonts and the page stylesheet land after the load event, so the frame keeps
        // reporting its own height instead of being measured once.
        this.bodyObserver = new ResizeObserver(this.updateHeight);
        this.bodyObserver.observe(frameDocument.body);
      },
      updateHeight() {
        // The observer can still fire on a body left behind by a navigation, whose document is
        // no longer reachable.
        const frameDocument = this.$refs.frame?.contentDocument;

        if (frameDocument) this.contentHeight = frameDocument.body.scrollHeight;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";

  // Elements are designed for a white page, and several carry white or transparent fields that
  // vanish on the dark theme's surface.
  .design-element-preview {
    background-color: get-color("same-white");
    border: 1px solid get-color("border");
    border-radius: 8px;
    overflow: hidden;

    &__viewport {
      min-height: 80px;
      transition: height 0.2s ease;
    }

    &__frame {
      display: block;
      border: 0;
      transform-origin: top left;
    }
  }
</style>
